<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardMessagePreview from './CardMessagePreview.vue';
import CardMessagePreviewWithMeta from './CardMessagePreviewWithMeta.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';

import ConversationContextMenu from 'dashboard/components/widgets/conversation/contextMenu/Index.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';

const props = defineProps({
  conversation: {
    type: Object,
    default: null,
  },
  chat: {
    type: Object,
    default: null,
  },
  contact: {
    type: Object,
    default: null,
  },
  stateInbox: {
    type: Object,
    default: null,
  },
  accountLabels: {
    type: Array,
    default: () => [],
  },
  activeLabel: { type: String, default: '' },
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  conversationType: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignLabel',
  'removeLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
]);

const router = useRouter();
const route = useRoute();
const store = useStore();

const cardMessagePreviewWithMetaRef = ref(null);

const normalizedConversation = computed(() => {
  const c = props.conversation || props.chat || {};
  return {
    ...c,
    unread_count: c.unread_count ?? c.unreadCount ?? 0,
    unreadCount: c.unreadCount ?? c.unread_count ?? 0,
    sla_policy_id: c.sla_policy_id ?? c.slaPolicyId,
    slaPolicyId: c.slaPolicyId ?? c.sla_policy_id,
    lastNonActivityMessage:
      c.lastNonActivityMessage ?? c.last_non_activity_message,
    customAttributes: c.customAttributes ?? c.custom_attributes ?? {},
  };
});

const currentChat = computed(() => store.getters.getSelectedChat || {});
const inboxesList = computed(() => store.getters['inboxes/getInboxes'] || []);
const activeInbox = computed(() => store.getters.getSelectedInbox);
const storeAccountId = computed(() => store.getters.getCurrentAccountId);

const senderId = computed(() => normalizedConversation.value.meta?.sender?.id);
const currentContact = computed(() => {
  if (props.contact) return props.contact;
  return senderId.value
    ? store.getters['contacts/getContact'](senderId.value) || {}
    : {};
});

const currentContactName = computed(() => currentContact.value?.name);
const currentContactThumbnail = computed(() => currentContact.value?.thumbnail);
const currentContactStatus = computed(
  () =>
    currentContact.value?.availabilityStatus ||
    currentContact.value?.availability_status
);

const inbox = computed(() => {
  if (props.stateInbox) return props.stateInbox;
  const id = normalizedConversation.value.inbox_id;
  return id ? store.getters['inboxes/getInbox'](id) || {} : {};
});

const inboxName = computed(() => inbox.value?.name);

const inboxIcon = computed(() => {
  const { channelType, medium } = inbox.value || {};
  return channelType ? getInboxIconByType(channelType, medium) : '';
});

const showInboxName = computed(() => {
  return (
    !props.hideInboxName && !activeInbox.value && inboxesList.value.length > 1
  );
});

const lastActivityAt = computed(() => {
  const timestamp = normalizedConversation.value.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const showMessagePreviewWithoutMeta = computed(() => {
  const { labels = [] } = normalizedConversation.value;
  return (
    !cardMessagePreviewWithMetaRef.value?.hasSlaThreshold && labels.length === 0
  );
});

const unreadCount = computed(
  () => normalizedConversation.value.unread_count || 0
);

const isActive = computed(() => {
  return currentChat.value.id === normalizedConversation.value.id;
});

const resolvedAccountId = computed(
  () => storeAccountId.value || route.params.accountId
);

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: resolvedAccountId.value,
      activeInbox: activeInbox.value,
      id: normalizedConversation.value.id,
      label: props.activeLabel,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  if (isActive.value) return;
  router.push({ path });
};

const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });

const resetState = () => {
  if (showContextMenu.value) {
    emit('contextMenuToggle', false);
  }
  hovered.value = false;
  showContextMenu.value = false;
  contextMenu.value = { x: null, y: null };
};

watch(() => normalizedConversation.value.id, resetState);

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', normalizedConversation.value.id, inbox.value.id);
  } else {
    emit(
      'deSelectConversation',
      normalizedConversation.value.id,
      inbox.value.id
    );
  }
};

const openContextMenu = e => {
  if (!props.enableContextMenu) return;
  e.preventDefault();
  emit('contextMenuToggle', true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit(
    'updateConversationStatus',
    normalizedConversation.value.id,
    status,
    snoozedUntil
  );
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [normalizedConversation.value.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [normalizedConversation.value.id]);
};

const onRemoveLabel = label => {
  emit('removeLabel', [label.title], [normalizedConversation.value.id]);
};

const onAssignTeam = team => {
  emit('assignTeam', team, normalizedConversation.value.id);
  closeContextMenu();
};

const markAsUnread = () => {
  emit('markAsUnread', normalizedConversation.value.id);
  closeContextMenu();
};

const markAsRead = () => {
  emit('markAsRead', normalizedConversation.value.id);
  closeContextMenu();
};

const assignPriority = priority => {
  emit('assignPriority', priority, normalizedConversation.value.id);
  closeContextMenu();
};

const deleteConversation = () => {
  emit('deleteConversation', normalizedConversation.value.id);
  closeContextMenu();
};

const accountLabels = computed(() => {
  if (props.accountLabels && props.accountLabels.length)
    return props.accountLabels;
  return store.getters['labels/getLabels'] || [];
});
</script>

<template>
  <div
    role="button"
    class="flex w-full gap-3 px-4 py-4 transition-all duration-300 ease-in-out cursor-pointer hover:bg-accent/40 active:scale-[0.99] border-b border-border/50 relative overflow-hidden group"
    :class="{
      'bg-accent/60 border-s-4 border-s-primary shadow-sm': isActive,
      'bg-n-purple-2 border-n-purple-4 dark:bg-n-purple-9': selected,
      'px-3 py-3': compact,
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <!-- ESLint validation for unused prop -->
    <span v-if="showAssignee" class="hidden" />
    <div
      class="relative flex-shrink-0 flex items-center"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContactName"
        :src="currentContactThumbnail"
        :size="compact ? 36 : 48"
        :status="currentContactStatus"
        class="shadow-sm group-hover:scale-105 transition-transform duration-300"
      >
        <template #overlay="{ size: avatarSize }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${avatarSize}px`, height: `${avatarSize}px` }"
            @click.stop
          >
            <input
              :value="selected"
              :checked="selected"
              class="!m-0 cursor-pointer"
              type="checkbox"
              @change="onSelectConversation($event.target.checked)"
            />
          </label>
        </template>
      </Avatar>
      <div
        v-if="unreadCount"
        class="absolute -top-1 -right-1 flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-[10px] font-bold text-white bg-primary rounded-full border-2 border-background shadow-sm"
      >
        {{ unreadCount }}
      </div>
    </div>

    <div class="flex flex-col w-full gap-1.5 min-w-0">
      <div class="flex items-center justify-between h-5 gap-2">
        <h4
          class="text-sm font-semibold truncate text-foreground group-hover:text-primary transition-colors duration-200"
        >
          {{ currentContactName }}
        </h4>
        <div class="flex items-center gap-1.5">
          <CardPriorityIcon
            :priority="normalizedConversation.priority || null"
          />
          <span
            class="text-[11px] font-medium text-muted-foreground whitespace-nowrap"
          >
            {{ lastActivityAt }}
          </span>
        </div>
      </div>

      <div class="flex items-center gap-2 min-w-0">
        <div
          v-if="showInboxName && inboxIcon"
          v-tooltip.top="inboxName"
          class="flex items-center justify-center flex-shrink-0 rounded-lg bg-muted size-5 border border-border/50"
        >
          <Icon
            :icon="inboxIcon"
            class="flex-shrink-0 text-muted-foreground size-3"
          />
        </div>
        <div class="flex-grow min-w-0">
          <CardMessagePreview
            v-show="showMessagePreviewWithoutMeta"
            :conversation="normalizedConversation"
            class="!text-xs !text-muted-foreground line-clamp-1"
          />
          <CardMessagePreviewWithMeta
            v-show="!showMessagePreviewWithoutMeta"
            ref="cardMessagePreviewWithMetaRef"
            :conversation="normalizedConversation"
            :account-labels="accountLabels"
            class="!text-xs !text-muted-foreground"
          />
        </div>
      </div>
    </div>

    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="normalizedConversation.status"
        :inbox-id="inbox.id"
        :priority="normalizedConversation.priority"
        :chat-id="normalizedConversation.id"
        :has-unread-messages="unreadCount > 0"
        :conversation-labels="normalizedConversation.labels"
        :conversation-url="conversationPath"
        :allowed-options="allowedContextMenuOptions"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @remove-label="onRemoveLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @delete-conversation="deleteConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
</template>
